import React, { useState } from 'react';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import DataGridMultiBrokerGroup from '@/components/table/DataGridComponents/DataGridMultiBrokerGroup';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useBatchResyncFormConfigMutation } from '@/features/config/configApi';
import { SCOPES } from '@/features/config/scopes';

/**
 * Where a row of the user's primary broker group was copied from (the default, a ledger or a
 * broker group), with a button that replaces the row with that config's current settings
 * after a confirm. Empty for rows from other scopes, and for rows that aren't copies.
 */
const CopiedFromCell = ({ config, disabled }) => {
	const dispatch = useDispatch();
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [batchResyncFormConfig] = useBatchResyncFormConfigMutation();

	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { copiedFrom } = config;
	const isOwnCopy =
		copiedFrom &&
		config.scope === SCOPES.BROKER_GROUP &&
		config.scopeId === userDetails?.brokerGroupId;
	if (!isOwnCopy) return null;

	const handleResync = async () => {
		const result = await batchResyncFormConfig({
			docIds: [config.id],
			configType: config.configType,
		});
		setIsConfirmOpen(false);
		if (result.error) {
			dispatch(
				showToast({ message: "Couldn't resync the row. Please try again.", error: true }),
			);
			return;
		}
		if (result.data.missing.length) {
			dispatch(
				showToast({
					message: 'The config this was copied from no longer exists.',
					error: true,
				}),
			);
			return;
		}
		dispatch(showToast({ message: 'Resynced the row', success: true }));
	};

	return (
		<Stack
			direction='row'
			spacing={0.5}
			sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}
		>
			{copiedFrom.scope === SCOPES.BROKER_GROUP && (
				<DataGridMultiBrokerGroup
					brokerGroupIds={[copiedFrom.scopeId]}
					showAdmin={userDetails?.isFolioAdmin}
				/>
			)}
			{copiedFrom.scope === SCOPES.LEDGER && <Chip size='small' label={copiedFrom.scopeId} />}
			{copiedFrom.scope === SCOPES.DEFAULT && (
				<Typography variant='body2' color='text.disabled'>
					Default
				</Typography>
			)}
			<Tooltip title='Resync row with parent'>
				<span>
					<IconButton
						size='small'
						aria-label='Resync row with parent'
						disabled={disabled}
						onClick={() => setIsConfirmOpen(true)}
					>
						<RefreshIcon fontSize='small' />
					</IconButton>
				</span>
			</Tooltip>
			<ConfirmDialog
				openDialog={isConfirmOpen}
				handleClose={() => setIsConfirmOpen(false)}
				handleConfirm={handleResync}
				title='Resync row?'
				content={`Replace ${config.label || config.name}'s settings with the current settings of the config it was copied from?`}
				confirmText='Resync'
			/>
		</Stack>
	);
};

export default CopiedFromCell;
